import React from 'react';
import { Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, MenuItem, Dialog, DialogContent, DialogTitle, IconButton, Checkbox, Button, FormControlLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InternalOrderService from '../../../components/InternalOrderService';
import ProductService from '../../../components/ProductService';
import ProductCategoryService from '../../../components/ProductCategoryService';
import { toast } from 'react-toastify';
import { useGetBalance } from '../api/queries';
import { useInventoryProducts } from '../hooks/useInventoryProducts';

interface Props {
  storeId: string;
  stores: Array<{ id: string; name: string }>;
}

const StockToFill: React.FC<Props> = ({ storeId, stores }) => {
  const [selectedStoreId, setSelectedStoreId] = React.useState<string>(storeId || (stores[0]?.id ?? ''));
  const balanceResult = useGetBalance({ store_id: selectedStoreId });
  const productsResult = useInventoryProducts();
  const data = balanceResult?.data ?? [];
  const products = productsResult?.data ?? [];

  const productMap = React.useMemo(() => {
    return new Map((products || []).map((p) => [p.id, p]));
  }, [products]);

  const [previewSrc, setPreviewSrc] = React.useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  const openPreview = (src?: string | null) => {
    if (!src) return;
    setPreviewSrc(src);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewSrc(null);
  };

  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [selectedPegasusCategoryId, setSelectedPegasusCategoryId] = React.useState<string>('');
  const [rawProductMap, setRawProductMap] = React.useState<Map<string, any>>(new Map());
  const [pegasusCategories, setPegasusCategories] = React.useState<Array<{ id: string; name: string }>>([]);

  React.useEffect(() => {
    let mounted = true;
    const buildRawProductMap = (items: any[]) => {
      return new Map<string, any>((items || []).map((p: any) => [String(p._id ?? p.id ?? ''), p]));
    };

    (async () => {
      try {
        const cats = await ProductCategoryService.fetchCategories();
        const items = Array.isArray(cats) ? cats : (cats?.response || []);
        const pegs = (items || []).filter(c => c && (c.type === 'pegasus')).map(c => ({ id: String(c._id ?? c.id ?? ''), name: c.name || c.label || String(c._id ?? c.id ?? '') }));
        if (mounted) setPegasusCategories(pegs);
      } catch (e) {
        // ignore
      }

      try {
        const resp = await ProductService.fetchProducts();
        // fetchProducts now returns an array directly from NDJSON stream
        const items = Array.isArray(resp) ? resp : [];
        const map = buildRawProductMap(items);
        if (mounted) setRawProductMap(map as Map<string, any>);
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    const handleProductsCacheUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ products?: any[] }>;
      const items = Array.isArray(customEvent.detail?.products) ? customEvent.detail.products : [];
      if (!items.length) {
        return;
      }
      setRawProductMap(new Map<string, any>(items.map((p: any) => [String(p._id ?? p.id ?? ''), p])));
    };

    window.addEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    return () => {
      window.removeEventListener(ProductService.PRODUCT_CACHE_UPDATED_EVENT, handleProductsCacheUpdated);
    };
  }, []);

  const rows = React.useMemo(() => {
    if (!data || !data.length) return [] as any[];
    return data.filter((r) => {
      if (r.stock_state !== 'available' || Number(r.quantity) !== 0) return false;
      const prod = productMap.get(r.product_id ?? '');
      const prodAny = prod as any;
      const rawProd = rawProductMap.get(String(r.product_id ?? ''));

      if (selectedPegasusCategoryId) {
        if (!rawProd) return false;
        const catId = String(rawProd.category ?? rawProd.category_id ?? rawProd.product_category ?? '');
        if (catId !== selectedPegasusCategoryId) return false;
      }

      if (searchQuery) {
        const name = ((prodAny?.name ?? rawProd?.name ?? prodAny?.label ?? r.product_id ?? '') + '').toString().toLowerCase();
        if (!name.includes(searchQuery.toLowerCase())) return false;
      }

      return true;
    });
  }, [data, productMap, searchQuery, selectedPegasusCategoryId, rawProductMap]);

  const [selected, setSelected] = React.useState<Record<string, number>>({});
  const [receiverStoreId, setReceiverStoreId] = React.useState<string>('');

  const toggleSelect = (productId: string) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[productId] != null) {
        delete next[productId];
      } else {
        next[productId] = 1;
      }
      return next;
    });
  };

  const setQuantity = (productId: string, qty: number) => {
    setSelected((s) => ({ ...s, [productId]: Math.max(0, Math.floor(qty || 0)) }));
  };

  const handleCreateInternalOrder = async () => {
    if (!selectedStoreId) {
      toast.error('Sélectionner un magasin source');
      return;
    }
    if (!receiverStoreId) {
      toast.error('Sélectionner un magasin receveur');
      return;
    }
    const productsPayload = Object.entries(selected)
      .map(([productId, qty]) => {
        // Only send product_id and quantity per requirement
        const prodId = String(productId || '').trim();
        if (!prodId) return null;
        return { product_id: prodId, quantity: qty };
      })
      .filter(Boolean);

    if (!productsPayload.length) {
      toast.error('Sélectionner au moins un produit avec une quantité');
      return;
    }

    const payload = {
      requester_store_id: selectedStoreId,
      receiver_store_id: receiverStoreId,
      products: productsPayload,
    };

    try {
      await InternalOrderService.createOrder(payload);
      toast.success('Commande interne créée');
      setSelected({});
      setReceiverStoreId('');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la création de la commande');
    }
  };

  if (!storeId) {
    return <Typography>Aucun magasin sélectionné.</Typography>;
  }

  if (balanceResult.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ gap: 2, flexWrap: 'wrap' }}>
            <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Stock to Fill</Typography>
            <TextField size="small" placeholder="Rechercher par nom..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <TextField
              select
              size="small"
              label="Catégorie Pegasus"
              value={selectedPegasusCategoryId}
              onChange={(e) => setSelectedPegasusCategoryId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Tous</MenuItem>
              {pegasusCategories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            select
            size="small"
            label="Magasin"
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {stores.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
        <TextField select size="small" label="Magasin receveur" value={receiverStoreId} onChange={(e) => setReceiverStoreId(e.target.value)} sx={{ minWidth: 220 }}>
          {stores.filter(s => !['external','glovo','site','partenaire'].includes((s as any).type)).map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" color="primary" onClick={handleCreateInternalOrder} disabled={Object.keys(selected).length === 0}>Créer commande interne ({Object.keys(selected).length})</Button>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }}>Select</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Barcode</TableCell>
              <TableCell>Store</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>State</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => {
              const prod = productMap.get(r.product_id ?? '') as any | undefined;
              const imageSrc = prod?.imageUrl || r.product_image || r.image || '';
              const productId = String(r.product_id ?? '');
              const isChecked = selected[productId] != null;
              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Checkbox checked={isChecked} onChange={() => toggleSelect(productId)} />
                  </TableCell>
                  <TableCell>
                    {imageSrc ? (
                      <Box component="img" src={imageSrc} alt={prod?.name ?? ''} sx={{ width: 48, height: 48, objectFit: 'cover', cursor: 'pointer', borderRadius: 1 }} onClick={() => openPreview(imageSrc)} />
                    ) : (
                      <Box sx={{ width: 48, height: 48, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>—</Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography noWrap>{prod?.name ?? r.product_id ?? '—'}</Typography>
                      {isChecked && (
                        <TextField size="small" type="number" value={selected[productId] ?? ''} onChange={(e) => setQuantity(productId, Number(e.target.value) || 0)} inputProps={{ min: 0, style: { width: 80 } }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{prod?.barcode ?? '—'}</TableCell>
                  <TableCell>{stores.find(s => s.id === r.store_id)?.name ?? r.store_id}</TableCell>
                  <TableCell align="right">{Number(r.quantity).toLocaleString()}</TableCell>
                  <TableCell>{r.stock_state}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">Aucun produit à remplir.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={previewOpen} onClose={closePreview} maxWidth="lg" fullWidth>
        <DialogTitle>
          Aperçu
          <IconButton aria-label="close" onClick={closePreview} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          {previewSrc && <Box component="img" src={previewSrc} alt="preview" sx={{ maxWidth: '100%', maxHeight: '80vh' }} />}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default StockToFill;
